/**
 * Sets --vh to 1% of window height to fix 100vh issues on mobile.
 * Use in CSS: height: calc(var(--vh) * 100);
 */
export function setAppHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

/**
 * Sets equal height for all .swiper-slide elements inside a given section.
 * Useful for aligning slides in Swiper sliders.
 */
export function setEqualHeight(section) {
  if (!section) return;

  const elements = section.querySelectorAll('.swiper-slide');
  if (elements.length === 0) return;

  let maxHeight = 0;

  elements.forEach(element => {
    element.style.height = 'auto';
  });

  elements.forEach(element => {
    const elementHeight = element.offsetHeight;
    if (elementHeight > maxHeight) {
      maxHeight = elementHeight;
    }
  });

  elements.forEach(element => {
    element.style.height = `${maxHeight}px`;
  });
}

/**
 * Throttles the resize event using requestAnimationFrame
 * and dispatches a custom 'optimizedResize' event instead.
 */
export function optimizedResize() {
  const throttle = function (type, name, obj = window) {
    let running = false;
    const func = function () {
      if (running) return;
      running = true;
      requestAnimationFrame(function () {
        obj.dispatchEvent(new CustomEvent(name));
        running = false;
      });
    };
    obj.addEventListener(type, func);
  };
  throttle('resize', 'optimizedResize');
}

/**
 * Adds tabindex="0" to all matching elements inside the given parent.
 * Makes them keyboard-focusable.
 *
 * @param {HTMLElement} parent - The parent container element.
 * @param {string} selector - CSS selector for target elements (default: 'a').
 */
export function addElsToTabindex(parent, selector = 'a') {
  if (!parent) return;

  const elements = parent.querySelectorAll(selector);

  elements.forEach((el) => {
    el.setAttribute('tabindex', 0);
  });
}

/**
 * Sets tabindex="-1" for all matching elements inside the given parent.
 * Removes them from the keyboard Tab navigation order.
 *
 * @param {HTMLElement} parent - The parent container element.
 * @param {string} selector - CSS selector for target elements (default: 'a').
 */
export function removeElsFromTabindex(parent, selector = 'a') {
  if (!parent) return;

  const elements = parent.querySelectorAll(selector);

  elements.forEach((el) => {
    el.setAttribute('tabindex', -1);
  });
}

/**
 * Watches focus inside a given container and triggers a callback
 * when focus leaves all matching elements (Tab navigation).
 *
 * @param {HTMLElement} container - The container to monitor.
 * @param {Function} onBlurCallback - Called when focus leaves.
 * @param {string} selector - CSS selector for focusable items (default: 'a').
 * @param {HTMLElement} [restoreFocusEl] - Element to restore focus to after blur.
 */
export function watchFocusWithin(container, onBlurCallback, selector = 'a', restoreFocusEl = null) {
  if (!container || typeof onBlurCallback !== 'function') return;

  const focusableEls = container.querySelectorAll(selector);

  const onKeyDown = (event) => {
    if (event.key !== 'Tab' && event.keyCode !== 9) return;

    requestAnimationFrame(() => {
      const isFocusedInside = Array.from(focusableEls).some(
        (el) => el === document.activeElement
      );

      if (!isFocusedInside) {
        onBlurCallback();

        if (restoreFocusEl && typeof restoreFocusEl.focus === 'function') {
          restoreFocusEl.focus();
        }

        document.removeEventListener('keydown', onKeyDown);
      }
    });
  };

  document.addEventListener('keydown', onKeyDown);
}
